import React, { useMemo, useState } from 'react'
import { noop } from 'lodash'
import {
  ConfirmationModal as ConfirmationModalComponent,
  useApplicationNotifications,
} from '@adc/ui-core'
import {
  genericErrorMessage,
  genericErrorTitle,
} from '../../../constants/errorMessages'

type ConfirmationModalProps = {
  close: () => void
  confirmButton: {
    onClick: () => Promise<void> | void
    label?: string
    role?: string
    disabled?: boolean
    isCountdownButton?: boolean
    reInitialize?: boolean
  }
  cancelButton: {
    onClick: () => void
    label?: string
    disabled?: boolean
  }
  formatConfirmError?: (err: unknown) => string
  onError?: (err: unknown) => void
  actionInfoText?: string
  preventClose?: boolean
  hideCancelButton?: boolean
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  cancelButton = {
    onClick: () => noop(),
  },
  confirmButton = {
    onClick: () => noop(),
  },
  close = () => noop(),
  onError = () => noop(),
  formatConfirmError,
  actionInfoText,
  preventClose = false,
  hideCancelButton = false,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const notifications = useApplicationNotifications()

  const onCancelModal = () => {
    cancelButton.onClick()
    !preventClose && close()
  }

  const onCloseModal = () => {
    cancelButton?.onClick()
    close()
  }

  const handleError = (err: unknown) => {
    const errorMessage =
      typeof formatConfirmError === 'function'
        ? formatConfirmError(err)
        : genericErrorMessage
    notifications.toast.open({
      role: 'error',
      title: genericErrorTitle,
      message: errorMessage,
    })
  }

  const onConfirmModal = async () => {
    setIsLoading(true)
    try {
      await confirmButton.onClick()
      !preventClose && close()
    } catch (err) {
      handleError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const cancelButtonProps = useMemo(
    () => ({
      ...cancelButton,
      label: cancelButton.label || 'Cancel',
      disabled: cancelButton.disabled || isLoading,
      onClick: onCancelModal,
    }),
    [cancelButton, onCancelModal]
  )

  const confirmButtonProps = useMemo(
    () => ({
      ...confirmButton,
      role: confirmButton.role || 'primary',
      label: confirmButton.label || 'Confirm',
      disabled: confirmButton.disabled || isLoading,
      loading: isLoading,
      onClick: onConfirmModal,
    }),
    [confirmButton, onConfirmModal]
  )

  return (
    <ConfirmationModalComponent
      open
      onClose={onCloseModal}
      onError={onError}
      actionInfoText={actionInfoText}
      hideCancelButton={hideCancelButton}
      cancelButtonProps={cancelButtonProps}
      confirmButtonProps={confirmButtonProps}
      {...rest}
    />
  )
}

export default ConfirmationModal
